import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface User {
  id: string
  nickname: string
  avatar: string
  phone: string
  points: number
  balance: number
  growth: number
  memberLevel: number
  createdAt: string
  gender?: string
  birthday?: string
  realName?: string
}

export interface Address {
  id: string
  province: string
  city: string
  district: string
  detail: string
  receiverName: string
  phone: string
  isDefault: boolean
}

interface UserStore {
  user: User | null
  isLoggedIn: boolean
  addresses: Address[]
  guestInitialized: boolean
  // 签到
  lastCheckin: string | null  // 上次签到日期 YYYY-MM-DD
  checkinStreak: number        // 连续签到天数
  checkinHistory: string[]     // 签到历史日期列表
  // 积分/余额明细
  pointsHistory: PointsRecord[]
  balanceHistory: BalanceRecord[]
  login: (user: User) => void
  logout: () => void
  initGuest: () => void
  addAddress: (address: Omit<Address, 'id'>) => string
  updateAddress: (id: string, address: Partial<Address>) => void
  removeAddress: (id: string) => void
  setDefaultAddress: (id: string) => void
  getDefaultAddress: () => Address | null
  addPoints: (points: number, reason?: string) => void
  deductPoints: (points: number, reason?: string) => boolean
  addBalance: (amount: number, reason?: string) => void
  deductBalance: (amount: number, reason?: string) => boolean
  updateProfile: (data: Partial<Pick<User, 'nickname' | 'avatar' | 'phone' | 'gender' | 'birthday' | 'realName'>>) => void
  checkin: () => { success: boolean; points: number; streak: number; isTodayChecked: boolean }
  getTodayChecked: () => boolean
}

export interface PointsRecord {
  id: string
  type: 'earn' | 'spend'
  amount: number
  reason: string
  createdAt: string
}

export interface BalanceRecord {
  id: string
  type: 'recharge' | 'spend' | 'refund'
  amount: number
  reason: string
  createdAt: string
}

function generateGuestId(): string {
  return 'G' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).substring(2, 6).toUpperCase()
}

function generateAddressId(): string {
  return 'addr_' + Date.now().toString(36) + Math.random().toString(36).substring(2, 5)
}

function generateRecordId(): string {
  return 'rec_' + Date.now().toString(36) + Math.random().toString(36).substring(2, 5)
}

function getTodayStr(): string {
  return new Date().toISOString().split('T')[0]
}

function isYesterday(dateStr: string): boolean {
  const d = new Date(dateStr)
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  return d.toISOString().split('T')[0] === yesterday.toISOString().split('T')[0]
}

export const useUserStore = create<UserStore>()(
  persist(
    (set, get) => ({
      user: null,
      isLoggedIn: false,
      addresses: [],
      guestInitialized: false,
      lastCheckin: null,
      checkinStreak: 0,
      checkinHistory: [],
      pointsHistory: [],
      balanceHistory: [],

      initGuest: () => {
        const state = get()
        if (state.guestInitialized && state.user) return

        const guestId = generateGuestId()
        const now = new Date().toLocaleString('zh-CN', { hour12: false }).replace(/\//g, '-')
        const guest: User = {
          id: guestId,
          nickname: `蛋糕爱好者${Math.floor(Math.random() * 9000 + 1000)}`,
          avatar: '',
          phone: '',
          points: 100,
          balance: 50,
          growth: 100,
          memberLevel: 1,
          createdAt: now,
        }

        // 默认地址
        const defaultAddress: Address = {
          id: generateAddressId(),
          province: '湖南省',
          city: '湘潭市',
          district: '岳塘区',
          detail: '建设南路88号万达广场附近',
          receiverName: guest.nickname,
          phone: '13800000000',
          isDefault: true,
        }

        set({
          user: guest,
          isLoggedIn: true,
          addresses: [defaultAddress],
          guestInitialized: true,
        })
      },

      login: (user) => set({ user, isLoggedIn: true, guestInitialized: true }),

      logout: () => set({
        user: null,
        isLoggedIn: false,
        addresses: [],
        guestInitialized: false,
      }),

      addAddress: (address) => {
        const id = generateAddressId()
        const newAddr = { ...address, id }
        const state = get()

        // 如果设为默认，取消其他默认
        const addresses = address.isDefault
          ? state.addresses.map(a => ({ ...a, isDefault: false }))
          : state.addresses

        set({ addresses: [...addresses, newAddr] })
        return id
      },

      updateAddress: (id, updates) => {
        set((state) => ({
          addresses: state.addresses.map(a => {
            if (a.id !== id) return a
            return { ...a, ...updates }
          }),
        }))
      },

      removeAddress: (id) => {
        const state = get()
        const addr = state.addresses.find(a => a.id === id)
        const addresses = state.addresses.filter(a => a.id !== id)

        // 如果删除的是默认地址，设置第一个为默认
        if (addr?.isDefault && addresses.length > 0) {
          addresses[0].isDefault = true
        }

        set({ addresses })
      },

      setDefaultAddress: (id) => {
        set((state) => ({
          addresses: state.addresses.map(a => ({
            ...a,
            isDefault: a.id === id,
          })),
        }))
      },

      getDefaultAddress: () => {
        return get().addresses.find(a => a.isDefault) || get().addresses[0] || null
      },

      addPoints: (points, reason = '其他') => {
        const state = get()
        if (!state.user) return
        const newGrowth = state.user.growth + points
        let newLevel = state.user.memberLevel
        if (newGrowth >= 10000) newLevel = 4
        else if (newGrowth >= 5000) newLevel = 3
        else if (newGrowth >= 1000) newLevel = 2

        set({
          user: {
            ...state.user,
            points: state.user.points + points,
            growth: newGrowth,
            memberLevel: newLevel,
          },
          pointsHistory: [
            { id: generateRecordId(), type: 'earn', amount: points, reason, createdAt: new Date().toISOString() },
            ...state.pointsHistory,
          ],
        })
      },

      deductPoints: (points, reason = '积分兑换') => {
        const state = get()
        if (!state.user || state.user.points < points) return false
        set({
          user: {
            ...state.user,
            points: state.user.points - points,
          },
          pointsHistory: [
            { id: generateRecordId(), type: 'spend', amount: -points, reason, createdAt: new Date().toISOString() },
            ...state.pointsHistory,
          ],
        })
        return true
      },

      addBalance: (amount, reason = '充值') => {
        const state = get()
        if (!state.user) return
        set({
          user: {
            ...state.user,
            balance: state.user.balance + amount,
          },
          balanceHistory: [
            { id: generateRecordId(), type: 'recharge', amount, reason, createdAt: new Date().toISOString() },
            ...state.balanceHistory,
          ],
        })
      },

      deductBalance: (amount, reason = '余额支付') => {
        const state = get()
        if (!state.user || state.user.balance < amount) return false
        set({
          user: {
            ...state.user,
            balance: state.user.balance - amount,
          },
          balanceHistory: [
            { id: generateRecordId(), type: 'spend', amount: -amount, reason, createdAt: new Date().toISOString() },
            ...state.balanceHistory,
          ],
        })
        return true
      },

      updateProfile: (data) => {
        const state = get()
        if (!state.user) return
        set({
          user: { ...state.user, ...data },
        })
      },

      checkin: () => {
        const state = get()
        const today = getTodayStr()

        if (state.lastCheckin === today) {
          return { success: false, points: 0, streak: state.checkinStreak, isTodayChecked: true }
        }

        // 计算连续签到
        let newStreak = 1
        if (state.lastCheckin && isYesterday(state.lastCheckin)) {
          newStreak = state.checkinStreak + 1
        }

        // 获取签到奖励（7天循环）
        const dayIndex = ((newStreak - 1) % 7)
        const { CHECKIN_RULES } = require('@/lib/constants')
        const rule = CHECKIN_RULES[dayIndex]
        const earnedPoints = rule?.points || 5

        // 添加积分
        const newGrowth = (state.user?.growth || 0) + earnedPoints
        let newLevel = state.user?.memberLevel || 1
        if (newGrowth >= 10000) newLevel = 4
        else if (newGrowth >= 5000) newLevel = 3
        else if (newGrowth >= 1000) newLevel = 2

        set({
          user: state.user ? {
            ...state.user,
            points: state.user.points + earnedPoints,
            growth: newGrowth,
            memberLevel: newLevel,
          } : state.user,
          lastCheckin: today,
          checkinStreak: newStreak,
          checkinHistory: [...state.checkinHistory, today],
          pointsHistory: [
            { id: generateRecordId(), type: 'earn' as const, amount: earnedPoints, reason: `连续签到第${newStreak}天`, createdAt: new Date().toISOString() },
            ...state.pointsHistory,
          ],
        })

        return { success: true, points: earnedPoints, streak: newStreak, isTodayChecked: false }
      },

      getTodayChecked: () => {
        return get().lastCheckin === getTodayStr()
      },
    }),
    {
      name: 'cake-shop-user',
    }
  )
)
